<script setup lang="ts">
import {
	ref,
	computed,
	watch,
	nextTick,
	onMounted,
	onBeforeUnmount,
} from 'vue';
import {
	SuiModal,
	SuiButton,
	SuiInput,
	SuiIcon,
	SuiInputVariant,
} from '@developers/scoro-ui';
import { useTranslation } from '@/composables';
import AppConfirmationPopover from '@/features/App/AppConfirmationPopover.vue';

export type FieldValueMapping = {
	scoroValue: string;
	newValue: string;
};

export type ScoroFieldConfig = {
	id: string;
	label: string;
	values: string[];
};

export type FieldMappingData = {
	fieldId: string;
	fieldLabel: string;
	valueMappings: FieldValueMapping[];
};

export type FieldMappingItem = {
	id?: string;
	name: string;
	fieldMappings: FieldMappingData[];
};

export type AvalaraFieldMappingModalProps = {
	isOpen: boolean;
	editingMapping?: FieldMappingItem | null;
	existingMappings?: Array<{ id?: string; name: string }>;
	unitOptions?: string[];
	taxCategoryOptions?: string[];
	paymentMethodOptions?: string[];
};

const props = withDefaults(defineProps<AvalaraFieldMappingModalProps>(), {
	editingMapping: null,
	existingMappings: () => [],
	unitOptions: () => [],
	taxCategoryOptions: () => [],
	paymentMethodOptions: () => [],
});

const emit = defineEmits<{
	(event: 'close'): void;
	(event: 'save', mapping: FieldMappingItem): void;
	(event: 'delete', mapping: FieldMappingItem): void;
}>();

const { t } = useTranslation();

const decapitalize = (value: string): string => {
	if (value.length === 0) {
		return value;
	}
	return value.charAt(0).toLowerCase() + value.slice(1);
};

const getDocumentTypeLabel = (value: string): string => {
	switch (value) {
		case 'Invoices':
			return t('spa__invoices.name');
		case 'Credit Invoices':
			return t('credit_invoices');
		case 'Bills':
			return t('bills');
		case 'Credit bills':
			return t('avalara_document_type_credit_bills');
		default:
			return value;
	}
};

const getScoroValueLabel = (fieldId: string, scoroValue: string): string => {
	if (fieldId === 'document_type') {
		return getDocumentTypeLabel(scoroValue);
	}
	return scoroValue;
};

const scoroFields = computed<ScoroFieldConfig[]>(() => [
	{
		id: 'unit',
		label: t('unit'),
		values: props.unitOptions,
	},
	{
		id: 'tax_category',
		label: t('avalara_field_mapping_tax_category'),
		values: props.taxCategoryOptions,
	},
	{
		id: 'document_type',
		label: t('avalara_field_mapping_document_type'),
		values: ['Invoices', 'Credit Invoices', 'Bills', 'Credit bills'],
	},
	{
		id: 'payment_method',
		label: t('avalara_field_mapping_payment_method'),
		values: props.paymentMethodOptions,
	},
]);

const MODAL_FADE_DURATION_MS = 150;
const modalVisible = ref(false);
const modalPhase = ref<'closed' | 'opening' | 'open' | 'closing'>('closed');
let closeTimeoutId: number | null = null;

const mappingName = ref('');
const selectedFieldId = ref<string>('unit'); // Default to first field
const fieldMappings = ref<Map<string, FieldValueMapping[]>>(new Map());
const modalContentRef = ref<HTMLElement | null>(null);
const hasAttemptedSave = ref(false);

let modalResizeObserver: ResizeObserver | null = null;

onMounted(() => {
	modalResizeObserver = new ResizeObserver(() => {
		const trigger = () => {
			window.dispatchEvent(new Event('resize'));
			window.dispatchEvent(new Event('scroll'));
		};
		trigger();
		requestAnimationFrame(trigger);
		setTimeout(trigger, 100);
		setTimeout(trigger, 500);
	});
});

onBeforeUnmount(() => {
	modalResizeObserver?.disconnect();
});

watch(
	modalContentRef,
	(el) => {
		if (el) {
			modalResizeObserver?.observe(el);
		} else {
			modalResizeObserver?.disconnect();
		}
	},
	{ immediate: true }
);

const isNameEmpty = computed(() => {
	return mappingName.value.trim() === '';
});

const isNameInvalid = computed(() => {
	return hasAttemptedSave.value && isNameEmpty.value;
});

const resetForm = () => {
	mappingName.value = '';
	fieldMappings.value = new Map();
	selectedFieldId.value = scoroFields.value[0]?.id ?? 'unit';
	hasAttemptedSave.value = false;
};

const initializeForm = () => {
	if (props.editingMapping) {
		mappingName.value = props.editingMapping.name;
		fieldMappings.value = new Map();

		// Build a lookup of saved values for quick access
		const savedFieldMappingsLookup = new Map<string, Map<string, string>>();
		for (const fm of props.editingMapping.fieldMappings) {
			const valueLookup = new Map<string, string>();
			for (const vm of fm.valueMappings) {
				valueLookup.set(vm.scoroValue, vm.newValue);
			}
			savedFieldMappingsLookup.set(fm.fieldId, valueLookup);
		}

		// For each field in scoroFields, merge saved values with all available values
		for (const field of scoroFields.value) {
			const savedValues = savedFieldMappingsLookup.get(field.id);
			const mergedMappings: FieldValueMapping[] = field.values.map(
				(scoroValue) => ({
					scoroValue,
					newValue: savedValues?.get(scoroValue) ?? '',
				})
			);
			fieldMappings.value.set(field.id, mergedMappings);
		}

		// Select first mapped field or first available field
		if (props.editingMapping.fieldMappings.length > 0) {
			selectedFieldId.value =
				props.editingMapping.fieldMappings[0].fieldId;
		} else {
			selectedFieldId.value = 'unit';
		}
	} else {
		mappingName.value = '';
		fieldMappings.value = new Map();
		selectedFieldId.value = 'unit';
	}
};

watch(
	() => props.isOpen,
	(isOpen) => {
		if (isOpen) {
			if (closeTimeoutId !== null) {
				window.clearTimeout(closeTimeoutId);
				closeTimeoutId = null;
			}

			modalVisible.value = true;
			modalPhase.value = 'opening';
			window.requestAnimationFrame(() => {
				modalPhase.value = 'open';
			});

			initializeForm();

			// Ensure first field is selected after DOM update
			nextTick(() => {
				if (!selectedFieldId.value || selectedFieldId.value === '') {
					selectedFieldId.value = scoroFields.value[0]?.id ?? 'unit';
				}
			});
		} else {
			if (modalVisible.value) {
				modalPhase.value = 'closing';
				closeTimeoutId = window.setTimeout(() => {
					modalVisible.value = false;
					modalPhase.value = 'closed';
					closeTimeoutId = null;
					resetForm();
				}, MODAL_FADE_DURATION_MS);
				return;
			}

			resetForm();
		}
	},
	{ immediate: true }
);

watch(
	() => props.editingMapping,
	(newMapping) => {
		if (props.isOpen && newMapping) {
			initializeForm();
		} else if (props.isOpen && !newMapping) {
			resetForm();
		}
	}
);

// Re-select first field when scoroFields changes (e.g., when props load)
watch(
	scoroFields,
	(fields) => {
		if (props.isOpen && fields.length > 0) {
			// Verify current selection is valid, otherwise reset to first
			const validIds = fields.map((f) => f.id);
			if (!validIds.includes(selectedFieldId.value)) {
				selectedFieldId.value = fields[0].id;
			}
		}
	},
	{ immediate: true }
);

const isEditing = computed(() => !!props.editingMapping?.id);
const modalTitle = computed(() =>
	isEditing.value ? t('avalara_modify_mapping') : t('avalara_add_mapping')
);

const selectedField = computed(() => {
	const found = scoroFields.value.find((f) => f.id === selectedFieldId.value);
	// Fallback to first field if current selection not found
	return found ?? scoroFields.value[0];
});

const selectedFieldColumnTitle = computed(() => {
	const field = selectedField.value;
	if (!field) {
		return '';
	}

	return `${field.label} ${decapitalize(t('avalara_field_mapping'))}`;
});

const currentFieldMappings = computed(() => {
	if (!selectedFieldId.value) {
		return [];
	}

	const existing = fieldMappings.value.get(selectedFieldId.value);
	if (existing) {
		return existing;
	}

	const field = selectedField.value;
	if (!field) {
		return [];
	}

	const defaultMappings: FieldValueMapping[] = field.values.map((v) => ({
		scoroValue: v,
		newValue: '',
	}));

	return defaultMappings;
});

const isFieldMapped = (fieldId: string): boolean => {
	const mappings = fieldMappings.value.get(fieldId);
	if (!mappings) {
		return false;
	}
	return mappings.some((m) => m.newValue.trim() !== '');
};

const selectField = (fieldId: string) => {
	if (selectedFieldId.value) {
		const current = currentFieldMappings.value;
		if (current.length > 0) {
			fieldMappings.value.set(selectedFieldId.value, [...current]);
		}
	}
	selectedFieldId.value = fieldId;
};

const updateValueMapping = (index: number, newValue: string) => {
	if (!selectedFieldId.value) {
		return;
	}

	const mappings = [...currentFieldMappings.value];
	mappings[index] = { ...mappings[index], newValue };
	fieldMappings.value.set(selectedFieldId.value, mappings);
};

const onValueInput = (index: number, event: Event) => {
	updateValueMapping(index, (event.target as HTMLInputElement).value);
};

const handleClose = () => {
	emit('close');
};

const handleSave = () => {
	hasAttemptedSave.value = true;

	if (isNameEmpty.value) {
		return;
	}

	if (selectedFieldId.value) {
		const current = currentFieldMappings.value;
		if (current.length > 0) {
			fieldMappings.value.set(selectedFieldId.value, [...current]);
		}
	}

	const mappingData: FieldMappingData[] = [];
	for (const [fieldId, valueMappings] of fieldMappings.value) {
		const field = scoroFields.value.find((f) => f.id === fieldId);
		const filteredValueMappings = valueMappings.filter(
			(m) => m.newValue.trim() !== ''
		);

		if (field && filteredValueMappings.length > 0) {
			mappingData.push({
				fieldId,
				fieldLabel: field.label,
				valueMappings: filteredValueMappings,
			});
		}
	}

	const result: FieldMappingItem = {
		id: props.editingMapping?.id,
		name: mappingName.value.trim(),
		fieldMappings: mappingData,
	};

	emit('save', result);
};

const handleDelete = () => {
	if (props.editingMapping) {
		emit('delete', props.editingMapping);
	}
};
</script>

<template>
	<SuiModal
		class="avalara-fm-modal"
		:class="{
			'avalara-fm-modal--open': modalPhase === 'open',
		}"
		:is-modal-open="modalVisible"
		:close-on-esc="false"
		@close="handleClose"
		@keydown.enter.prevent
	>
		<template #header>
			<div class="avalara-fm-modal__header">
				<span class="avalara-fm-modal__title">
					{{ t('avalara_field_mapping') }}
				</span>
				<SuiIcon
					class="avalara-fm-modal__breadcrumb-arrow"
					name="icon-arrow-right-16"
				/>
				<span class="avalara-fm-modal__subtitle">
					{{ modalTitle }}
				</span>
			</div>
		</template>

		<template #default>
			<div ref="modalContentRef" class="avalara-fm-modal__content">
				<!-- Info banner -->
				<div class="avalara-fm-modal__info-banner">
					<div class="avalara-fm-modal__info-icon">
						<SuiIcon name="icon-info-24" />
					</div>
					<div class="avalara-fm-modal__info-text">
						{{ t('avalara_field_mapping_info') }}
					</div>
				</div>

				<!-- Name input -->
				<div
					class="avalara-fm-modal__name-field"
					:class="{
						'avalara-fm-modal__field--placeholder-overlay':
							isNameEmpty,
					}"
				>
					<span
						v-if="isNameEmpty"
						class="avalara-fm-modal__placeholder-with-dot"
						aria-hidden="true"
					>
						<span class="avalara-fm-modal__placeholder-text">
							{{ t('name') }}
						</span>
						<span class="avalara-fm-modal__placeholder-dot">
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width="4"
								height="4"
								viewBox="0 0 4 4"
							>
								<circle
									cx="2"
									cy="2"
									r="2"
									fill="currentColor"
								/>
							</svg>
						</span>
					</span>
					<SuiInput
						v-model="mappingName"
						:placeholder="t('name')"
						size="small"
						:variant="
							isNameInvalid ? SuiInputVariant.ERROR : undefined
						"
						:message="
							isNameInvalid
								? t('avalara_mandate_name_required')
								: undefined
						"
					/>
				</div>

				<!-- Two column layout -->
				<div class="avalara-fm-modal__columns">
					<!-- Left column: Scoro fields -->
					<div class="avalara-fm-modal__left-column">
						<div class="avalara-fm-modal__column-title">
							{{ t('avalara_scoro_field') }}
						</div>
						<!-- Header row -->
						<div class="avalara-fm-modal__field-list">
							<button
								v-for="field in scoroFields"
								:key="field.id"
								type="button"
								class="avalara-fm-modal__field-item"
								:class="{
									'avalara-fm-modal__field-item--selected':
										selectedFieldId === field.id,
									'avalara-fm-modal__field-item--mapped':
										isFieldMapped(field.id),
								}"
								@click="selectField(field.id)"
							>
								<span class="avalara-fm-modal__field-label">
									{{ field.label }}
								</span>
								<SuiIcon
									v-if="isFieldMapped(field.id)"
									class="avalara-fm-modal__field-check"
									:class="{
										'avalara-fm-modal__field-check--selected':
											selectedFieldId === field.id,
										'avalara-fm-modal__field-check--unselected':
											selectedFieldId !== field.id,
									}"
									name="icon-checkbox-16"
								/>
							</button>
						</div>
					</div>

					<div class="avalara-fm-modal__right-column">
						<div
							v-if="selectedField"
							class="avalara-fm-modal__column-title"
						>
							{{ selectedFieldColumnTitle }}
						</div>
						<div
							v-if="selectedField"
							class="avalara-fm-modal__value-mappings"
						>
							<!-- Header row -->
							<div class="avalara-fm-modal__value-header">
								<span
									class="avalara-fm-modal__value-header-scoro"
								>
									{{ t('avalara_scoro_value') }}
								</span>
								<span
									class="avalara-fm-modal__value-header-new"
								>
									{{ t('avalara_new_value') }}
								</span>
							</div>

							<!-- Value rows -->
							<div
								v-for="(mapping, index) in currentFieldMappings"
								:key="mapping.scoroValue"
								class="avalara-fm-modal__value-row"
							>
								<span class="avalara-fm-modal__value-scoro">
									{{
										getScoroValueLabel(
											selectedFieldId,
											mapping.scoroValue
										)
									}}
								</span>
								<input
									type="text"
									class="avalara-fm-modal__value-input"
									:aria-label="`${t('avalara_new_value')}: ${getScoroValueLabel(selectedFieldId, mapping.scoroValue)}`"
									:value="mapping.newValue"
									@input="onValueInput(index, $event)"
								/>
							</div>
						</div>
					</div>
				</div>
			</div>
		</template>

		<template #footer>
			<div class="avalara-fm-modal__footer">
				<div class="avalara-fm-modal__footer-left">
					<AppConfirmationPopover
						v-if="isEditing"
						v-slot="{ openConfirmation }"
						:title="t('delete_mapping')"
						message="general_delete_confirmation_message"
						@confirm="handleDelete"
					>
						<button
							type="button"
							class="avalara-fm-modal__delete-btn"
							:title="t('delete')"
							@click="openConfirmation(props.editingMapping)"
						>
							<SuiIcon name="icon-bin-16" />
						</button>
					</AppConfirmationPopover>
				</div>
				<div class="avalara-fm-modal__footer-center">
					<SuiButton variant="primary" @click="handleSave">
						{{ t('apply') }}
					</SuiButton>
				</div>
				<div class="avalara-fm-modal__footer-right" />
			</div>
		</template>
	</SuiModal>
</template>

<style lang="scss" scoped>
.avalara-fm-modal {
	--sui-modal-max-width: 800px;
	--sui-modal-min-width: 800px;

	:deep(.popup-modal__container) {
		background: rgb(31 34 38 / 15%);
		opacity: 0;
		transition: opacity 0.15s ease;
	}

	:deep(.popup-modal__item) {
		position: relative;
		background: $white;
		border-radius: 0.5rem;
		overflow: hidden;
		box-shadow:
			0 2px 6px 0 rgb(31 34 38 / 8%),
			0 8px 32px 0 rgb(31 34 38 / 12%);
		opacity: 0;
		transform: translateY(0.5rem) scale(0.98);
		transition:
			opacity 0.15s ease,
			transform 0.15s ease;
	}

	&--open {
		:deep(.popup-modal__container) {
			opacity: 1;
		}

		:deep(.popup-modal__item) {
			opacity: 1;
			transform: translateY(0) scale(1);
		}
	}

	:deep(.popup-modal__header),
	:deep(.popup-modal__content),
	:deep(.popup-modal__footer) {
		border-radius: 0;
	}

	:deep(.popup-modal__header) {
		background: $neutral-10;
		border-bottom: 1px solid $neutral-20;
		padding: 2rem 2rem 2rem 3rem;
		min-height: 7rem;
	}

	:deep(.popup-modal__content) {
		background: $white;
		padding: 0;
	}

	:deep(.popup-modal__footer) {
		background: $neutral-10;
		padding: 2rem;
		min-height: 8rem;
	}

	:deep(.popup-modal__close) {
		top: 2rem;
		right: 2rem;
		width: 3rem;
		height: 3rem;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	&__header {
		display: flex;
		align-items: center;
		gap: 1rem;
	}

	&__title {
		font-family: Inter, sans-serif;
		font-size: 16px;
		font-weight: 600;
		line-height: 22px;
		color: $neutral-90;
	}

	&__breadcrumb-arrow {
		color: $neutral-60;
	}

	&__subtitle {
		font-family: Inter, sans-serif;
		font-size: 14px;
		font-weight: 400;
		line-height: 16px;
		color: $neutral-70;
	}

	&__content {
		display: flex;
		flex-direction: column;
		gap: 3rem;
		padding: 3rem;
	}

	&__info-banner {
		display: flex;
		align-items: stretch;
		border: 1px solid $blue-20;
		border-radius: 4px;
		overflow: hidden;
	}

	&__info-icon {
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 1rem;
		background: $blue-10;
		border: 1px solid $blue-20;
		color: $blue-50;

		:deep(.sui-icon),
		:deep(.icon) {
			color: $blue-50;
			fill: $blue-50;
		}

		:deep(svg) {
			fill: $blue-50;
		}
	}

	&__info-text {
		flex: 1;
		padding: 2rem;
		font-family: Inter, sans-serif;
		font-size: 12px;
		font-weight: 400;
		line-height: 16px;
		color: $neutral-90;
	}

	&__name-field {
		width: 100%;
		position: relative;
	}

	&__field--placeholder-overlay {
		:deep(input::placeholder) {
			opacity: 0;
		}

		:deep(textarea::placeholder) {
			opacity: 0;
		}

		&:focus-within,
		&:has(input:not(:placeholder-shown)),
		&:has(textarea:not(:placeholder-shown)) {
			.avalara-fm-modal__placeholder-with-dot {
				opacity: 0;
			}
		}
	}

	&__placeholder-with-dot {
		position: absolute;
		left: 1rem;
		top: 50%;
		transform: translateY(-50%);
		display: inline-flex;
		align-items: flex-start;
		gap: 0.5rem;
		pointer-events: none;
		z-index: 3;
	}

	&__placeholder-text {
		color: $neutral-70;
		font-family: Inter, sans-serif;
		font-size: 1.5rem;
		font-style: normal;
		font-weight: 400;
		line-height: 2rem;
	}

	&__placeholder-dot {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 0.5rem;
		height: 0.5rem;
		color: $orange-50;
		margin-top: 0.25rem;
	}

	&__columns {
		display: flex;
		border: 1px solid $neutral-30;
		border-radius: 8px;
		min-height: 400px;
	}

	&__left-column {
		display: flex;
		flex-direction: column;
		gap: 2rem;
		width: 200px;
		flex-shrink: 0;
		padding: 3rem 0;
		border-right: 1px solid $neutral-30;
	}

	&__right-column {
		display: flex;
		flex-direction: column;
		gap: 2rem;
		flex: 1;
		padding: 3rem 5rem;
	}

	&__column-title {
		font-family: Inter, sans-serif;
		font-size: 14px;
		font-weight: 600;
		line-height: 18px;
		color: $blue-50;
		padding: 0 2rem;
	}

	&__right-column &__column-title {
		padding: 0;
	}

	&__field-list {
		display: flex;
		flex-direction: column;
	}

	&__field-item {
		display: flex;
		align-items: center;
		justify-content: space-between;
		height: 40px;
		padding: 1.5rem 2rem;
		border: none;
		border-bottom: 1px solid $neutral-20;
		background: transparent;
		cursor: pointer;
		text-align: left;

		&:hover {
			background: $neutral-10;
		}

		&--selected {
			background: $blue-10;

			.avalara-fm-modal__field-label {
				font-weight: 600;
				color: $blue-90;
			}
		}

		&--mapped:not(&--selected) {
			.avalara-fm-modal__field-label {
				color: $green-90;
			}
		}
	}

	&__field-label {
		font-family: Inter, sans-serif;
		font-size: 12px;
		font-weight: 400;
		line-height: 16px;
		color: $neutral-70;
	}

	&__field-check {
		&--selected {
			color: $blue-50 !important;
			fill: $blue-50 !important;

			:deep(svg),
			:deep(path) {
				color: $blue-50 !important;
				fill: $blue-50 !important;
			}
		}

		&--unselected {
			color: $green-50 !important;
			fill: $green-50 !important;

			:deep(svg),
			:deep(path) {
				color: $green-50 !important;
				fill: $green-50 !important;
			}
		}
	}

	&__value-mappings {
		display: flex;
		flex-direction: column;
	}

	&__value-header {
		display: flex;
		gap: 2rem;
		padding: 1rem 0;
		border-bottom: 1px solid $neutral-20;
		font-family: Inter, sans-serif;
		font-size: 12px;
		font-weight: 400;
		line-height: 16px;
		color: $neutral-70;
	}

	&__value-header-scoro {
		width: 160px;
	}

	&__value-header-new {
		width: 120px;
	}

	&__value-row {
		display: flex;
		align-items: center;
		gap: 2rem;
		padding: 1rem 0;
		border-bottom: 1px solid $neutral-20;
	}

	&__value-scoro {
		width: 160px;
		font-family: Inter, sans-serif;
		font-size: 12px;
		font-weight: 400;
		line-height: 16px;
		color: $neutral-90;
	}

	&__value-input {
		width: 120px;
		height: 32px;
		padding: 1rem;
		border: 1px solid $neutral-40;
		border-radius: 4px;
		font-family: Inter, sans-serif;
		font-size: 12px;
		font-weight: 400;
		line-height: 16px;
		color: $neutral-90;

		&::placeholder {
			color: $neutral-70;
		}

		&:focus {
			outline: none;
			border-color: $blue-50;
		}
	}

	&__footer {
		display: flex;
		justify-content: space-between;
		align-items: center;
		width: 100%;
	}

	&__footer-left {
		display: flex;
		align-items: center;
		flex: 1;
	}

	&__footer-center {
		display: flex;
		align-items: center;
		justify-content: center;
		flex: 1;
	}

	&__footer-right {
		display: flex;
		align-items: center;
		flex: 1;
		justify-content: flex-end;
	}

	&__delete-btn {
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 1rem;
		border: none;
		background: transparent;
		cursor: pointer;
		color: $neutral-70;
		border-radius: 4px;

		&:hover {
			background: $neutral-10;
			color: $red-60;
		}
	}
}
</style>
